type ChatListRouteInput = {
  id: string
  type?: string | null
  unread?: number | null
  single_unread_topic_id?: string | null
}

type ChatParentTab = 'lanes' | 'groups' | 'code'

export type ChatTarget = {
  chatId: string
  topicId: string | null
  messageId: string | null
}

// The Lanes chat list is the app's home. The explicit ?tab=lanes forces the tab
// when there is no nearer parent.
export const CHATS_HOME = '/chats?tab=lanes'
export const GROUPS_HOME = '/chats?tab=groups'
export const CODE_HOME = '/chats?tab=code'
export const FREE_LANES_HOME = '/chats?tab=lanes&filter=free'
export const FIRST_FREE_LANE_ROUTE = '/chats/first-free'

// The single Todoist Tasks group. The TODO tab opens it directly (its topic
// list), so "TODO" everywhere means this group's topics, never an intermediate
// one-row list. Stable backend id (lane_control seed_data / controller).
export const TODO_CHAT_ID = 'pwa-auto-todoist-tasks'
export const X_LINKS_CHAT_ID = 'pwa-auto-x-links'
const AUTO_SPAWN_CHAT_ID_PREFIX = 'pwa-auto-'
// Coding groups are auto-spawn chats, but they live in their own Code tab
// rather than under Groups, so Back from one must resolve to Code, not Groups.
const CODE_CHAT_ID_PREFIX = 'pwa-auto-code-'

/** The coding agents Mark can hold a conversation with, in tab order. The Code
 *  tab is a single conversation list ACROSS these chats — the per-group list is
 *  never a destination — so this array is the one place the set is declared.
 *  (A Cursor coding group was deliberately archived; do not re-add one here.) */
export const CODE_AGENTS = [
  { agent: 'claude', label: 'Claude', chatId: 'pwa-auto-code-claude' },
  { agent: 'codex', label: 'Codex', chatId: 'pwa-auto-code-codex' },
] as const

export type CodeAgent = (typeof CODE_AGENTS)[number]['agent']

export const CODE_CHAT_IDS: readonly string[] = CODE_AGENTS.map((c) => c.chatId)

/** Which coding agent owns a chat id, or null when it is not a coding group. */
export function codeChatAgent(chatId: string): CodeAgent | null {
  return CODE_AGENTS.find((c) => c.chatId === chatId)?.agent ?? null
}

// One Back press in from the Lanes home: collapse whatever chat filter is active
// down to the "Done" view. The active filter lives in the URL (?filter=, exactly
// like ?tab=) so this stays a pure function of the URL — the back button reads it
// directly. From the Done view Back resolves to null (the root) and does nothing;
// AgentChat is never exited by Back. See ChatListPage's ?filter= handling.
const CHATS_HOME_DONE = '/chats?tab=lanes&filter=done'

function isGroupChatId(chatId: string): boolean {
  return chatId.startsWith(AUTO_SPAWN_CHAT_ID_PREFIX)
}

export function isCodeChatId(chatId: string): boolean {
  return chatId.startsWith(CODE_CHAT_ID_PREFIX)
}

function parentTabForChat(chatId: string, params: URLSearchParams): ChatParentTab {
  if (params.get('tab') === 'lanes') return 'lanes'
  // Code is checked first: a coding group matches both prefixes, and its
  // parent is the Code tab.
  if (params.get('tab') === 'code' || isCodeChatId(chatId)) return 'code'
  return params.get('tab') === 'groups' || isGroupChatId(chatId) ? 'groups' : 'lanes'
}

function homeForParentTab(parentTab: ChatParentTab): string {
  if (parentTab === 'code') return CODE_HOME
  return parentTab === 'groups' ? GROUPS_HOME : CHATS_HOME
}

/**
 * The single source of truth for "where does Back go?". Every back affordance —
 * the in-app header arrow and the Android hardware/gesture back (via
 * window.__agentchatHandleAndroidBack) — resolves through this one pure
 * function of the URL.
 *
 * Returns the URL to navigate to, or null when the user is already at the app
 * root (the Lanes home) or on a pre-auth shell. A null result means Back does
 * NOTHING: AgentChat is never exited by Back — only the Home button leaves.
 */
export function backTarget(pathname: string, search: string = ''): string | null {
  const path = pathname.replace(/\/+$/, '') || '/'
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  // Pre-auth shells have nowhere to go "up" to; Back is inert there.
  if (path === '/login' || path === '/setup') return null

  // Chat / group / topic hierarchy.
  if (path.startsWith('/chats/')) {
    const encodedChatId = path.slice('/chats/'.length).split('/')[0]
    const chatId = decodeURIComponent(encodedChatId)
    const topicId = params.get('topic_id') || params.get('topic')
    const parentTab = parentTabForChat(chatId, params)
    // Topic open → up to its chat/group homepage.
    if (encodedChatId && topicId) return parentTab === 'lanes' && isGroupChatId(chatId)
      ? CHATS_HOME
      : chatTarget(chatId, null, { parentTab })
    // A group homepage → Groups tab, a coding group → Code tab, a lane → Lanes.
    return homeForParentTab(parentTab)
  }

  // The home chat list. Back walks a fixed path toward the root:
  //   Groups → Lanes home → Lanes "Done" view → (root: do nothing). On the
  //   Lanes home, Back collapses whatever filter is active — including none —
  //   down to Done, one tap from the empty root; from Done it does nothing. The
  //   app is never exited by Back.
  if (path === '/' || path === '/chats') {
    const tab = params.get('tab')
    if (tab === 'code') return CHATS_HOME
    if (tab === 'groups') return CHATS_HOME
    if (params.get('filter') === 'done') return null
    return CHATS_HOME_DONE
  }

  if (path === '/search') {
    const scopeChatId = params.get('chat_id')
    if (scopeChatId) return chatTarget(scopeChatId, params.get('topic_id') || params.get('topic'))
    return CHATS_HOME
  }

  if (path === '/schedules' || path === '/reminders') return '/settings'
  if (path === '/commitments') return '/chats/pwa-auto-commitments'
  if (path === '/events') return '/chats/pwa-auto-event-tickets'
  if (path === '/architecture-campaigns') return '/chats/pwa-auto-architecture-deepen'

  // Every other in-app surface (settings, features, skills, logs, …) → home.
  return CHATS_HOME
}

export function chatTarget(
  chatId: string,
  topicId: string | null = null,
  options: { parentTab?: ChatParentTab } = {},
): string {
  const path = `/chats/${encodeURIComponent(chatId)}`
  const params = new URLSearchParams()
  if (options.parentTab === 'lanes') params.set('tab', 'lanes')
  if (options.parentTab === 'groups') params.set('tab', 'groups')
  if (options.parentTab === 'code') params.set('tab', 'code')
  if (!topicId) {
    const query = params.toString()
    return query ? `${path}?${query}` : path
  }
  params.set('chat_id', chatId)
  params.set('topic_id', topicId)
  return `${path}?${params.toString()}`
}

type CodeConversationRoute = {
  chatId: string
  topicId: string
}

/** Resolve where the split-pane Code view should go after an inline archive.
 * Archiving a background row must not steal focus; archiving the selected row
 * advances through the unified Claude/Codex inbox, then falls back to Code. */
export function codeArchiveDestination(
  currentUrl: URL,
  archived: CodeConversationRoute,
  active: readonly CodeConversationRoute[],
): string | null {
  const current = chatTargetFromUrl(currentUrl)
  if (current?.chatId !== archived.chatId || current.topicId !== archived.topicId) {
    return null
  }
  const next = active.find((candidate) => (
    candidate.chatId !== archived.chatId || candidate.topicId !== archived.topicId
  ))
  return next
    ? chatTarget(next.chatId, next.topicId, { parentTab: 'code' })
    : CODE_HOME
}

export function chatListDirectUnreadTopicId(chat: ChatListRouteInput): string | null {
  if (chat.type !== 'auto-spawn') return null
  if ((chat.unread ?? 0) <= 0) return null
  return chat.single_unread_topic_id ?? null
}

export function chatListTarget(chat: ChatListRouteInput): string {
  return chatTarget(chat.id, chatListDirectUnreadTopicId(chat), {
    parentTab: chat.type === 'auto-spawn' ? 'groups' : 'lanes',
  })
}

export function chatLocationUrl(pathname: string, search: string = '', hash: string = ''): string {
  return `${pathname}${search}${hash}`
}

export function chatTargetFromUrl(url: URL): ChatTarget | null {
  const match = url.pathname.match(/^\/chats\/([^/]+)$/)
  const chatId = match?.[1] ? decodeURIComponent(match[1]) : url.searchParams.get('chat_id')
  if (!chatId) return null
  return {
    chatId,
    topicId: url.searchParams.get('topic_id') || url.searchParams.get('topic') || null,
    messageId: url.searchParams.get('message_id') || url.searchParams.get('message') || null,
  }
}

export function canonicalChatUrl(url: URL): string {
  const target = chatTargetFromUrl(url)
  if (!target) return `${url.pathname}${url.search}`
  const params = new URLSearchParams(url.search)
  const notificationOpen = params.get('notification_open') === '1'
  params.delete('chat_id')
  params.delete('topic')
  params.delete('topic_id')
  params.delete('message')
  params.delete('notification_open')
  if (target.topicId) {
    params.set('chat_id', target.chatId)
    params.set('topic_id', target.topicId)
  }
  if (target.messageId) params.set('message_id', target.messageId)
  else params.delete('message_id')
  if (notificationOpen) params.set('notification_open', '1')
  const query = params.toString()
  return `/chats/${encodeURIComponent(target.chatId)}${query ? `?${query}` : ''}`
}

export function canonicalActiveChatRouteUrl(url: URL): string {
  if (!url.pathname.match(/^\/chats\/[^/]+$/)) return `${url.pathname}${url.search}`
  return canonicalChatUrl(url)
}

export function sameChatRoute(
  currentPathname: string,
  currentSearch: string,
  targetHref: string,
): boolean {
  const base = 'https://agentchat.local'
  const current = new URL(`${currentPathname}${currentSearch}`, base)
  const target = new URL(targetHref, base)
  if (!current.pathname.match(/^\/chats\/[^/]+$/)) return false
  if (current.searchParams.get('notification_open') === '1') return false
  const currentTarget = chatTargetFromUrl(current)
  const targetTarget = chatTargetFromUrl(target)
  if (currentTarget?.messageId) return false
  return sameChatTarget(currentTarget, targetTarget)
}

export function sameChatTarget(a: ChatTarget | null, b: ChatTarget | null): boolean {
  return !!a && !!b && a.chatId === b.chatId && a.topicId === b.topicId
}
